import { FC, useContext, useMemo, useState, useEffect } from 'react';
import { Box, Image, Text, Flex, Avatar, Icon } from 'src/components/ui';
import {
	ICreative,
	isCreatomateAttributes,
} from 'src/lib/schemas/campaign/newFlowCampaign';
import { IChannelCreativeAttributes } from 'src/lib/schemas';

import FusionLoading from 'src/components/common/FusionLoading';
import TemplatePreview from 'src/components/templates/TemplatePreview';
import DDPhotoEditor, {
	DDPhotoEditorProps,
} from '../../design-directions/DDPhotoEditor';
import CreativeCard from '../CreativeCard';
import { FiChevronRight } from 'react-icons/fi';
import UserContext from 'src/contexts/user/UserContext';

interface HorizontalCreativeProps extends Partial<DDPhotoEditorProps> {
	creative: ICreative;
	withActions?: boolean;
	size?: string;
	creativesConfig?: IChannelCreativeAttributes[];
	editMode?: boolean;
	isPaid?: boolean;
}

const HorizontalCreative: FC<HorizontalCreativeProps> = ({
	creative,
	withActions = true,
	size = 'md',
	creativesConfig,
	editMode = false,
	isPaid,
	...photoEditorProps
}) => {
	const { account } = useContext(UserContext);
	const [logo, setLogo] = useState<string | undefined>(account?.logoDark || account?.logoSquare);

	useEffect(() => {
		setLogo(account?.logoDark || account?.logoSquare);
	}, [account]);

	const isPending =
		creative.status === 'pending' || creative.status === 'generating';

	const isCreatomate =
		creative.adEngine === 'creatomate' &&
		isCreatomateAttributes(creative.attributes);

	const normalizedTemplate = useMemo(() => {
		if (isCreatomate) {
			const clone = JSON.parse(JSON.stringify(creative.attributes));
			if (Array.isArray(clone.elements)) {
				clone.elements = clone.elements.map((el: any) => {
					if (typeof el.x === 'string') el.x = el.x.trim();
					if (typeof el.y === 'string') el.y = el.y.trim();
					if (typeof el.width === 'string') el.width = el.width.trim();
					if (typeof el.height === 'string') el.height = el.height.trim();
					if (el.elements && Array.isArray(el.elements)) {
						el.elements = el.elements.map((subEl: any) => {
							if (typeof subEl.x === 'string') subEl.x = subEl.x.trim();
							if (typeof subEl.y === 'string') subEl.y = subEl.y.trim();
							if (typeof subEl.width === 'string')
								subEl.width = subEl.width.trim();
							if (typeof subEl.height === 'string')
								subEl.height = subEl.height.trim();
							return subEl;
						});
					}
					return el;
				});
			}
			return clone;
		}
		return null;
	}, [JSON.stringify(creative.attributes)]);

	return (
		<CreativeCard
			creative={creative}
			withActions={withActions}
			size="lg"
			title="Horizontal"
			creativesConfig={creativesConfig}
		>
			{isPending ? (
				<Flex width="100%" minH={{ base: '400px', md: '677px' }} justifyContent="center">
					<FusionLoading isLoading />
				</Flex>
			) : (
				<Box p={{ base: 4, md: 8 }} width="100%">
					<Box
						border="1px solid"
						borderColor="gray.300"
						borderRadius="md"
						overflow="hidden"
					>
						<Box h={{ base: '180px', md: '233px' }}>
							{isCreatomate && normalizedTemplate ? (
								<TemplatePreview
									key={JSON.stringify((creative as any).attributes.elements)}
									template={normalizedTemplate}
								/>
							) : !editMode ? (
								<Image
									key={(creative as any).attributes.image?.flatFile}
									src={(creative as any).attributes.image?.flatFile}
									alt="Preview"
									fallbackSrc="/fusion-loader.gif"
									borderRadius="5px"
									h="full"
									overflow="hidden"
								/>
							) : (
								photoEditorProps.designDirection &&
								photoEditorProps.isChangingLayer !== undefined &&
								photoEditorProps.onRefetchCreatives && (
									<DDPhotoEditor
										key={photoEditorProps.layeredFile}
										layeredFile={photoEditorProps.layeredFile}
										textLayersContent={photoEditorProps.textLayersContent}
										mediaLayersContent={photoEditorProps.mediaLayersContent}
										fonts={photoEditorProps.fonts}
										designDirection={photoEditorProps.designDirection}
										isChangingLayer={photoEditorProps.isChangingLayer}
										onRefetchCreatives={photoEditorProps.onRefetchCreatives}
										withActions={false}
										containerHeight="382px"
									/>
								)
							)}
						</Box>
						<Flex align="center" justify="space-between" p={3} px={6}>
							<Flex align="center" gap={2}>
								<Avatar
									key={logo || 'no-logo'}
									src={logo}
									sx={{
										img: { objectFit: 'contain' },
									}}
								/>
								<Text fontWeight="bold" isTruncated={true}>
									{account?.name}
								</Text>
							</Flex>
							<Icon
								as={FiChevronRight as any}
								color="orange.base"
								w={10}
								h={10}
							/>
						</Flex>
					</Box>
				</Box>
			)}
		</CreativeCard>
	);
};

export default HorizontalCreative;
